import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { AdapterService } from '../services/adapter.service';
import { GatewayService } from '../services/gateway.service';
import * as GatewayActions from './gateway.actions';

@Injectable()
export class GatewayEffects {
    private _actions$ = inject(Actions);
    private _gatewayService = inject(GatewayService);
    private _alertService = inject(AlertService);
    private _adapter = inject(AdapterService);

    messages = {
        getAllError: 'Error getting all gateways',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Gateways
    getAllGateways$ = createEffect(() =>
        this._actions$.pipe(
            ofType(GatewayActions.loadGateways),
            switchMap((action) => {
                return this._gatewayService.getAll(action.params).pipe(
                    map((res) => {
                        const data = this._adapter.adaptGatewayData(
                            res.data.gateways
                        );
                        return GatewayActions.loadGatewaysSuccess({
                            gateways: data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            GatewayActions.loadGatewaysFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get Gateways by Production
    getGatewaysByProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(GatewayActions.loadGatewaysByProduction),
            switchMap((action) => {
                return this._gatewayService
                    .getAllByProduction(action.params)
                    .pipe(
                        map((res) => {
                            const data = this._adapter.adaptGatewayData(
                                res.data
                            );
                            return GatewayActions.loadGatewaysByProductionSuccess(
                                {
                                    gateways: data,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                GatewayActions.loadGatewaysByProductionFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One Gateway
    getGateway$ = createEffect(() =>
        this._actions$.pipe(
            ofType(GatewayActions.loadGateway),
            switchMap((action) => {
                return this._gatewayService.getOne(action.id).pipe(
                    map((res) => {
                        return GatewayActions.loadGatewaySuccess({
                            gateway: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            GatewayActions.loadGatewayFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Gateway
    createGateway$ = createEffect(() =>
        this._actions$.pipe(
            ofType(GatewayActions.createGateway),
            switchMap((action) => {
                return this._gatewayService.create(action.data).pipe(
                    map((res) => {
                        const data = this._adapter.adaptSingleGatewayData(
                            res.data
                        );
                        return GatewayActions.createGatewaySuccess({
                            gateway: data,
                            success: 'Gateway created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            GatewayActions.createGatewayFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Gateway
    updateGateway$ = createEffect(() =>
        this._actions$.pipe(
            ofType(GatewayActions.updateGateway),
            switchMap((action) => {
                return this._gatewayService.update(action.data, action.id).pipe(
                    map((res) => {
                        return GatewayActions.updateGatewaySuccess({
                            gateway: res.data,
                            success: 'Gateway updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            GatewayActions.updateGatewayFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Gateway
    deleteGateway$ = createEffect(() =>
        this._actions$.pipe(
            ofType(GatewayActions.deleteGateway),
            switchMap((action) => {
                return this._gatewayService.delete(action.id).pipe(
                    map(() => {
                        return GatewayActions.deleteGatewaySuccess({
                            id: action.id,
                            success: 'Gateway deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            GatewayActions.deleteGatewayFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onGatewayActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    GatewayActions.createGatewaySuccess,
                    GatewayActions.updateGatewaySuccess,
                    GatewayActions.deleteGatewaySuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onGatewayActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    GatewayActions.loadGatewaysFailure,
                    GatewayActions.loadGatewayFailure,
                    GatewayActions.createGatewayFailure,
                    GatewayActions.updateGatewayFailure,
                    GatewayActions.deleteGatewayFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
